import { useTranslation } from "react-i18next";
import { useStoreWithEqualityFn } from "zustand/traditional";

import {
  campaignAttention,
  campaignKey,
  type CampaignAttention,
} from "@/campaigns";
import type { SwitchableCampaign } from "@/hooks/use-switchable-campaigns";
import { useAppStore } from "@/stores";

type AttentionByKey = Record<string, CampaignAttention>;

const sameAttention = (a: AttentionByKey, b: AttentionByKey): boolean => {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every(
    (k) => b[k] && b[k].tone === a[k].tone && b[k].label === a[k].label
  );
};

/**
 * News per switchable campaign, keyed by campaign key — what the in-game selector marks with a
 * dot on the cover (and announces once with a bubble). Re-evaluated on store updates (the
 * streams push at least every 30 s, often enough for "Must Drop Now"); consumers only re-render
 * when a campaign gains, loses or changes its news.
 */
export const useSwitcherAttention = (
  campaigns: SwitchableCampaign[]
): AttentionByKey => {
  const { t } = useTranslation();
  return useStoreWithEqualityFn(
    useAppStore,
    (s) => {
      const out: AttentionByKey = {};
      for (const { campaign, owned } of campaigns) {
        const attention = campaignAttention(s, owned, t);
        if (attention) out[campaignKey(campaign)] = attention;
      }
      return out;
    },
    sameAttention
  );
};
